/**
 * @fileoverview Transactions Service - Handles all transaction-related API operations
 * @description This service manages financial transactions including income, expenses, and transfers
 * @version 1.0.0
 * @author Dashboard Team
 * @created 2024-01-01
 * @updated 2024-01-01
 */

import { format as formatDateFns } from 'date-fns';
import { api } from './api';
import { logger } from './loggerService';
import { createApiCacheKey, cacheApiResponse, getCachedApiResponse } from '../utils/cache';
import { transactionMonthDateRange, transactionYearDateRange } from '../utils/calendarPeriod';
import type {
  CreateExpenseOrchestrationResponse,
  CreateExpenseTransactionPayload,
  CreateIncomeOrchestrationResponse,
  CreateIncomeTransactionPayload,
  ExpensePaymentStatus,
  IncomePaymentStatus,
  TransactionsListData,
} from '../types/apiContracts';
import {
  descriptionLooksLikeCategoryToken,
  descriptionLooksLikeContactOnly,
  descriptionLooksLikeInternalArApLine,
  isArApSourcedTransactionRecord,
  resolveArApContactName,
  resolveArApSourceDescription,
  resolveArApTransactionLinkIds,
  sanitizeDisplayReference,
} from '../utils/transactionArApDescription';

export type TransactionType = 'income' | 'expense' | 'transfer';

export type { ExpensePaymentStatus, IncomePaymentStatus, CreateExpenseTransactionPayload, CreateIncomeTransactionPayload };

export type TransactionPaymentStatus = ExpensePaymentStatus | IncomePaymentStatus;

/** List row from GET /transactions — minimal fields from API. */
export interface TransactionListItem {
  id: string;
  type: TransactionType;
  amount: number;
  status?: string;
  created_at: string;
}

export interface TransactionItem {
  id: string;
  date: string; // ISO date
  description: string;
  amount: number; // positive for income, negative for expense
  type: TransactionType;
  category: string;
  payment_method?: string;
  reference_number?: string;
  status?: string;
  vendor?: string | undefined;
  client?: string | undefined;
      contact_name?: string | undefined;
  contact_id?: string | undefined;
  contact_type?: 'client' | 'vendor' | 'employee' | 'other';
      gst_number?: string | undefined;
  /** Set when transaction was posted from a receivable settlement */
  receivable_id?: string;
  /** Set when transaction was posted from a payable settlement */
  payable_id?: string;
  ar_ap_source?: 'receivable' | 'payable';
  /** Expense: paid/unpaid. Income: received/not_received. */
  payment_status?: TransactionPaymentStatus;
  /** Vendor payable due date (unpaid expense) */
  payable_due_date?: string;
  /** Client receivable due date (not-received income) */
  receivable_due_date?: string;
  /** Income only — false = draft, excluded from report totals */
  invoice_raised?: boolean;
  /** Gross invoice amount; may differ from abs(amount) when TDS applies */
  gross_amount?: number;
  tds_applicable?: boolean;
  tds_percent?: number;
  tds_amount?: number;
  /** Net amount owed to vendor after TDS (expense) */
  net_payable_amount?: number;
  /** Net amount expected from client after TDS (income) */
  net_receivable_amount?: number;
  /** Linked TDS statutory due */
  statutory_due_id?: string;
  /** TDS credit status when linked due is receivable */
  tds_credit_status?: 'pending' | 'credited';
}

export const TRANSACTION_PAYMENT_METHODS = [
  'Cash',
  'Card',
  'UPI',
  'Bank Transfer',
  'Cheque',
  'NEFT',
  'RTGS',
  'Auto Debit',
  'Other',
] as const;

export interface TransactionFilters {
  page?: number;
  limit?: number;
  type?: TransactionType | 'all';
  category?: string | 'all';
  payment_method?: string | 'all';
  start_date?: string; // ISO
  end_date?: string;   // ISO
  month?: number;      // 0-11 for January-December
  year?: number;       // Full year (e.g., 2024)
  search?: string;
  organizationId?: string | null; // For cache key - ensures per-org cache, no stale data on org switch
  /** When true, skip the short-lived in-memory response cache (e.g. Reports month breakdown). */
  skipCache?: boolean;
}

export interface TransactionsResponse {
  transactions: TransactionItem[];
  pagination: { page: number; limit: number; total: number; totalPages?: number };
  totals: { income: number; expense: number; net: number };
  categories: string[];
}

/** Shown when list mapping finds no text; UI may hydrate from GET /transactions/:id. */
export const TRANSACTION_MISSING_DESCRIPTION_LABEL = 'No description' as const;

/**
 * Bumped whenever transaction mutations clear the list cache.
 * In-flight GETs that started before a bump must not re-seed the apiCache
 * with a pre-mutation payload (classic stale list after add).
 */
let transactionsListCacheGeneration = 0;

/** Call from refresh/invalidation paths before any post-mutation refetch. */
export function bumpTransactionsListCacheGeneration(): void {
  transactionsListCacheGeneration += 1;
}

/** Format list/detail transaction dates without throwing on empty/invalid values. */
export function formatTransactionListDate(
  date: string | undefined | null,
  pattern = 'MMM dd, yyyy'
): string {
  const raw = (date ?? '').trim();
  if (!raw) return '—';
  const parsed = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(parsed.getTime())) return '—';
  try {
    return formatDateFns(parsed, pattern);
  } catch {
    return raw.slice(0, 10);
  }
}

/** Prepend a newly created row into a cached list response (page 1 style). */
export function prependTransactionToListResponse(
  old: TransactionsResponse | undefined,
  created: TransactionItem
): TransactionsResponse | undefined {
  if (!old?.transactions) return old;
  if (old.transactions.some((t) => t.id === created.id)) return old;

  const limit = old.pagination?.limit ?? old.transactions.length ?? 20;
  const transactions = [created, ...old.transactions].slice(0, limit);
  const gross = Math.abs(created.amount || 0);
  const income =
    created.type === 'income' && created.invoice_raised !== false
      ? (old.totals?.income ?? 0) + gross
      : old.totals?.income ?? 0;
  const expense =
    created.type === 'expense'
      ? (old.totals?.expense ?? 0) + gross
      : old.totals?.expense ?? 0;

  return {
    ...old,
    transactions,
    pagination: {
      ...old.pagination,
      page: old.pagination?.page ?? 1,
      limit,
      total: (old.pagination?.total ?? old.transactions.length) + 1,
    },
    totals: {
      income,
      expense,
      net: income - expense,
    },
    categories: old.categories?.includes(created.category)
      ? old.categories
      : [...(old.categories ?? []), created.category].filter(Boolean),
  };
}

export function transactionNeedsDetailHydration(description: string | undefined): boolean {
  return (
    !description?.trim() ||
    description === TRANSACTION_MISSING_DESCRIPTION_LABEL ||
    descriptionLooksLikeInternalArApLine(description)
  );
}

/** List rows often omit category; merge with GET /transactions/:id like the Transactions table. */
export function transactionNeedsCategoryHydration(category: string | undefined): boolean {
  const c = (category ?? '').trim();
  return !c || c === '—' || c === '-';
}

/** Match Transactions page: merge list row + detail when list DTO is thin. */
export function mergeListAndDetailTransaction(
  row: TransactionItem,
  detail: TransactionItem
): TransactionItem {
  const description = (() => {
    const rowBad = transactionNeedsDetailHydration(row.description);
    const detailText = detail.description?.trim() ?? '';
    const detailGood =
      detailText.length > 0 &&
      detailText !== TRANSACTION_MISSING_DESCRIPTION_LABEL &&
      !descriptionLooksLikeInternalArApLine(detailText);
    if (rowBad && detailGood) return detail.description;
    if (!rowBad) return row.description;
    if (detailGood) return detail.description;
    return row.description;
  })();
  const category =
    row.category?.trim() && row.category !== '—'
      ? row.category
      : detail.category?.trim() && detail.category !== '—'
        ? detail.category
        : row.category;

  const payment_method = row.payment_method || detail.payment_method;
  const reference_number = sanitizeDisplayReference(
    row.reference_number || detail.reference_number,
    { ...(detail as unknown as Record<string, unknown>), ...(row as unknown as Record<string, unknown>) }
  );
  const contact_name = row.contact_name || detail.contact_name;
  const vendor = row.vendor || detail.vendor;
  const client = row.client || detail.client;
  const gst_number = row.gst_number || detail.gst_number;
  const contact_type = row.contact_type || detail.contact_type;
  const status = row.status || detail.status;
  const receivable_id = row.receivable_id || detail.receivable_id;
  const payable_id = row.payable_id || detail.payable_id;
  const ar_ap_source = row.ar_ap_source || detail.ar_ap_source;
  const payment_status = row.payment_status || detail.payment_status;
  const payable_due_date = row.payable_due_date || detail.payable_due_date;
  const receivable_due_date = row.receivable_due_date || detail.receivable_due_date;
  const gross_amount = row.gross_amount ?? detail.gross_amount;
  const tds_applicable = row.tds_applicable ?? detail.tds_applicable;
  const tds_percent = row.tds_percent ?? detail.tds_percent;
  const tds_amount = row.tds_amount ?? detail.tds_amount;
  const net_payable_amount = row.net_payable_amount ?? detail.net_payable_amount;
  const statutory_due_id = row.statutory_due_id || detail.statutory_due_id;
  const contact_id = row.contact_id || detail.contact_id;

  const base: TransactionItem = {
    ...row,
    description,
    category,
  };

  return {
    ...base,
    ...(payment_method ? { payment_method } : {}),
    ...(reference_number ? { reference_number } : {}),
    ...(contact_name ? { contact_name } : {}),
    ...(vendor ? { vendor } : {}),
    ...(client ? { client } : {}),
    ...(gst_number ? { gst_number } : {}),
    ...(contact_type ? { contact_type } : {}),
    ...(status ? { status } : {}),
    ...(receivable_id ? { receivable_id } : {}),
    ...(payable_id ? { payable_id } : {}),
    ...(ar_ap_source ? { ar_ap_source } : {}),
    ...(payment_status ? { payment_status } : {}),
    ...(payable_due_date ? { payable_due_date: payable_due_date.slice(0, 10) } : {}),
    ...(receivable_due_date ? { receivable_due_date: receivable_due_date.slice(0, 10) } : {}),
    ...(gross_amount != null ? { gross_amount } : {}),
    ...(tds_applicable != null ? { tds_applicable } : {}),
    ...(tds_percent != null ? { tds_percent } : {}),
    ...(tds_amount != null ? { tds_amount } : {}),
    ...(net_payable_amount != null ? { net_payable_amount } : {}),
    ...(statutory_due_id ? { statutory_due_id } : {}),
    ...(contact_id ? { contact_id } : {}),
  };
}

export function needsTransactionPickerHydration(row: TransactionItem): boolean {
  return (
    transactionNeedsDetailHydration(row.description) ||
    transactionNeedsCategoryHydration(row.category) ||
    !row.payment_method?.trim()
  );
}

function firstNonEmptyString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return undefined;
}

/**
 * Category on list/detail rows — API may send string, nested object, or `category_name` aliases.
 */
function resolveTransactionCategory(t: Record<string, any>): string {
  const cat = t.category;
  if (typeof cat === 'string') {
    const s = cat.trim();
    if (s) return s;
  } else if (typeof cat === 'number' && Number.isFinite(cat)) {
    return String(cat);
  } else if (cat && typeof cat === 'object') {
    const nested = firstNonEmptyString(
      cat.name,
      cat.label,
      cat.title,
      typeof cat.value === 'string' ? cat.value : typeof cat.value === 'number' ? String(cat.value) : undefined,
      typeof cat.slug === 'string' ? cat.slug : undefined,
      typeof cat.code === 'string' ? cat.code : undefined
    );
    if (nested) return nested;
  }

  const flat = firstNonEmptyString(
    t.category_name,
    t.categoryName,
    t.category_label,
    t.categoryLabel,
    t.category_display_name,
    t.categoryDisplayName,
    t.expense_category,
    t.income_category,
    t.transaction_category,
    t.transactionCategory,
    t.subcategory,
    t.sub_category,
    t.budget_category,
    t.cost_center_name,
    t.cost_center,
    typeof t.account === 'object' && t.account
      ? firstNonEmptyString(
          (t.account as Record<string, unknown>).name,
          (t.account as Record<string, unknown>).label,
          (t.account as Record<string, unknown>).title
        )
      : undefined,
    typeof t.account === 'string' ? t.account : undefined,
    t.account_name,
    t.accountName,
    t.ledger_name,
    t.ledgerName,
    t.gl_account_name,
    t.chart_account_name,
    Array.isArray(t.tags) && t.tags.length && typeof (t.tags as unknown[])[0] === 'string'
      ? String((t.tags as unknown[])[0])
      : undefined,
    typeof t.tags === 'string' ? t.tags : undefined
  );
  return flat ?? '—';
}

/**
 * Human-readable description for list/detail rows.
 * Minimal list DTOs often omit `description` but send memo, notes, narration, etc.
 */
function resolveTransactionDescription(t: Record<string, any>): string {
  const contactName = resolveContactName(t);
  const categoryRaw = resolveTransactionCategory(t);
  const arApSourceDesc = resolveArApSourceDescription(t);
  const arApSourced = isArApSourcedTransactionRecord(t);

  const direct = firstNonEmptyString(
    t.description,
    t.memo,
    t.notes,
    t.note,
    t.title,
    arApSourced ? undefined : t.name,
    t.text,
    t.subject,
    t.narration,
    t.particulars,
    t.summary,
    t.purpose,
    t.label,
    t.transaction_description,
    t.transactionDescription,
    t.transaction_note,
    t.details?.description,
    t.meta?.description,
    t.remark,
    t.remarks
  );

  const directUsable =
    direct &&
    !descriptionLooksLikeInternalArApLine(direct) &&
    !descriptionLooksLikeCategoryToken(direct, categoryRaw !== '—' ? categoryRaw : undefined) &&
    !descriptionLooksLikeContactOnly(direct, contactName);

  if (arApSourced) {
    if (arApSourceDesc) return arApSourceDesc;
    if (directUsable) return direct;
    return '';
  }

  if (directUsable) return direct;
  if (arApSourceDesc) return arApSourceDesc;

  if (Array.isArray(t.line_items) && t.line_items.length > 0) {
    const li = t.line_items[0] as Record<string, unknown>;
    const lineDesc = firstNonEmptyString(
      li.description,
      li.memo,
      typeof li.notes === 'string' ? li.notes : undefined,
      typeof li.title === 'string' ? li.title : undefined
    );
    if (lineDesc) return lineDesc;
  }

  const categoryResolved = resolveTransactionCategory(t);
  const category = categoryResolved !== '—' ? categoryResolved : '';
  const refRaw = t.reference_number ?? t.reference;
  const ref = sanitizeDisplayReference(
    typeof refRaw === 'string' ? refRaw : undefined,
    t
  ) ?? '';

  if (category && ref && !arApSourced) return `${category} · ${ref}`;
  if (ref && !arApSourced) return ref;
  if (category && !arApSourced) return category;

  const contact = contactName ?? firstNonEmptyString(
    t.contact_name,
    t.contact?.name,
    typeof t.contact === 'string' ? t.contact : undefined,
    t.vendor_name,
    t.client_name,
    t.vendor,
    t.client,
    t.payee,
    t.payer
  );
  if (contact) return contact;

  return TRANSACTION_MISSING_DESCRIPTION_LABEL;
}

function resolvePaymentMethod(t: Record<string, any>): string | undefined {
  return firstNonEmptyString(
    t.payment_method,
    t.paymentMethod,
    t.payment_mode,
    t.paymentMode,
    t.payment_type,
    t.paymentType
  );
}

function resolveContactName(t: Record<string, any>): string | undefined {
  return firstNonEmptyString(
    t.contact_name,
    t.contact?.name,
    typeof t.contact === 'string' ? t.contact : undefined,
    t.vendor_name,
    t.client_name,
    t.vendor,
    t.client,
    t.payee,
    t.payer,
    resolveArApContactName(t)
  );
}

/** List/detail row — optional keys omitted when empty (exactOptionalPropertyTypes). */
function normalizeTransactionType(raw: unknown, rawAmount: number): TransactionType {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (value === 'expense' || value === 'transfer' || value === 'income') {
    return value;
  }
  // List DTOs sometimes omit type — infer from signed amount when present.
  if (rawAmount < 0) return 'expense';
  return 'income';
}

/** Prefer booking/transaction date — never payable/receivable due dates for list DATE column. */
function resolveListTransactionDate(t: Record<string, any>): string {
  const prefer = firstNonEmptyString(
    t.transaction_date,
    t.transactionDate,
    t.txn_date,
    t.txnDate,
    t.booking_date,
    t.bookingDate
  );
  if (prefer) {
    const raw = prefer.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    // Local y/m/d — avoid UTC day shift from toISOString()
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  const due = firstNonEmptyString(
    t.payable_due_date,
    t.payableDueDate,
    t.receivable_due_date,
    t.receivableDueDate,
    t.due_date,
    t.dueDate
  );
  const genericDate = firstNonEmptyString(t.date);
  // If API stuffed due date into `date`, ignore it for the transaction column.
  if (
    genericDate &&
    due &&
    genericDate.slice(0, 10) === due.slice(0, 10)
  ) {
    const created = firstNonEmptyString(t.created_at, t.createdAt);
    if (!created) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(created)) return created.slice(0, 10);
    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  const fallback = firstNonEmptyString(genericDate, t.created_at, t.createdAt);
  if (!fallback) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(fallback)) return fallback.slice(0, 10);
  const d = new Date(fallback);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Keep rows whose transaction date is in [start, endExclusive). */
function filterTransactionsByHalfOpenDateRange(
  transactions: TransactionItem[],
  start: string,
  endExclusive: string
): TransactionItem[] {
  return transactions.filter((t) => {
    const d = (t.date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
    return d >= start && d < endExclusive;
  });
}

function transactionItemFromApiRecord(t: Record<string, any>): TransactionItem {
  const rawAmount = parseFloat(String(t.amount ?? 0));
  const type = normalizeTransactionType(t.type, rawAmount);
  const signed =
    type === 'expense' || type === 'transfer'
      ? -Math.abs(rawAmount)
      : Math.abs(rawAmount);
  const created = resolveListTransactionDate(t);
  const payment = resolvePaymentMethod(t);
  const refRaw = t.reference_number ?? t.reference;
  const ref = sanitizeDisplayReference(
    typeof refRaw === 'string' ? refRaw.trim() : undefined,
    t
  );
  const contactName = resolveContactName(t);
  const vendorStr =
    t.vendor !== undefined && t.vendor !== null ? String(t.vendor).trim() : '';
  const clientStr =
    t.client !== undefined && t.client !== null ? String(t.client).trim() : '';
  const gstStr =
    t.gst_number !== undefined && t.gst_number != null
      ? String(t.gst_number).trim()
      : '';

  const arApLink = resolveArApTransactionLinkIds(t);

  const base: TransactionItem = {
    id: String(t.id),
    date: created,
    description: resolveTransactionDescription(t),
    amount: signed,
    type,
    category: resolveTransactionCategory(t),
  };

  return {
    ...base,
    ...(payment ? { payment_method: payment } : {}),
    ...(ref ? { reference_number: ref } : {}),
    ...(t.status != null && String(t.status).trim()
      ? { status: String(t.status).trim() }
      : {}),
    ...(vendorStr ? { vendor: vendorStr } : {}),
    ...(clientStr ? { client: clientStr } : {}),
    ...(contactName ? { contact_name: contactName } : {}),
    ...(t.contact_type ? { contact_type: t.contact_type } : {}),
    ...(gstStr ? { gst_number: gstStr } : {}),
    ...(arApLink.receivable_id ? { receivable_id: arApLink.receivable_id } : {}),
    ...(arApLink.payable_id ? { payable_id: arApLink.payable_id } : {}),
    ...(arApLink.ar_ap_source ? { ar_ap_source: arApLink.ar_ap_source } : {}),
    ...(t.payment_status === 'paid' ||
    t.payment_status === 'unpaid' ||
    t.payment_status === 'received' ||
    t.payment_status === 'not_received'
      ? { payment_status: t.payment_status as TransactionPaymentStatus }
      : {}),
    ...(() => {
      const payableDue = firstNonEmptyString(t.payable_due_date, t.payableDueDate);
      return payableDue ? { payable_due_date: payableDue.slice(0, 10) } : {};
    })(),
    ...(() => {
      const recvDue = firstNonEmptyString(t.receivable_due_date, t.receivableDueDate);
      return recvDue ? { receivable_due_date: recvDue.slice(0, 10) } : {};
    })(),
    ...(t.invoice_raised != null ? { invoice_raised: Boolean(t.invoice_raised) } : {}),
    ...(t.gross_amount != null ? { gross_amount: parseFloat(String(t.gross_amount)) } : {}),
    ...(t.tds_applicable != null ? { tds_applicable: Boolean(t.tds_applicable) } : {}),
    ...(t.tds_percent != null ? { tds_percent: parseFloat(String(t.tds_percent)) } : {}),
    ...(t.tds_amount != null ? { tds_amount: parseFloat(String(t.tds_amount)) } : {}),
    ...(t.net_payable_amount != null
      ? { net_payable_amount: parseFloat(String(t.net_payable_amount)) }
      : {}),
    ...(t.net_receivable_amount != null
      ? { net_receivable_amount: parseFloat(String(t.net_receivable_amount)) }
      : {}),
    ...(t.tds_credit_status === 'pending' || t.tds_credit_status === 'credited'
      ? { tds_credit_status: t.tds_credit_status }
      : {}),
    ...(t.statutory_due_id != null && String(t.statutory_due_id).trim()
      ? { statutory_due_id: String(t.statutory_due_id) }
      : {}),
    ...(t.contact_id != null && String(t.contact_id).trim()
      ? { contact_id: String(t.contact_id) }
      : {}),
  };
}

/**
 * @service TransactionsService
 * @description Main service object for managing financial transactions
 */
export const transactionsService = {
  /**
   * Full transaction for detail drawer / edit — GET /transactions/:id
   */
  async getTransactionById(id: string): Promise<TransactionItem> {
    const response = await api.get(`/transactions/${id}`);
    const payload = (response.data?.data ?? response.data) as Record<string, any> | undefined;
    const t = (
      payload && typeof payload === 'object' && payload.transaction && typeof payload.transaction === 'object'
        ? payload.transaction
        : payload
    ) as Record<string, any>;
    return transactionItemFromApiRecord(t);
  },

  /**
   * @method getTransactions
   * @description Fetches transactions with optional filtering and pagination
   * @param filters - Optional filters to apply (type, category, date range, search, etc.)
   * @returns Promise<TransactionsResponse> - Transactions data with pagination and totals
   * @throws Error if API call fails
   * @example
   * ```typescript
   * const data = await transactionsService.getTransactions({ 
   *   type: 'expense', 
   *   page: 1, 
   *   limit: 20 
   * });
   * ```
   */
  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionsResponse> {
    // Prefer explicit date ranges over month/year. Sending month+year together (or year
    // alongside start/end) has caused 400s; month filters historically appeared "broken".
    let startDate = filters.start_date;
    let endDate = filters.end_date;
    let month = filters.month;
    let year = filters.year;
    /** When set, trim rows to this half-open window (guards inclusive API end bounds). */
    let boundStart: string | undefined;
    let boundEndExclusive: string | undefined;

    if ((!startDate || !endDate) && month !== undefined && month >= 0 && year !== undefined) {
      const range = transactionMonthDateRange(year, month);
      startDate = range.start;
      endDate = range.endExclusive;
      month = undefined;
      year = undefined;
    } else if (
      !startDate &&
      !endDate &&
      (month === undefined || month < 0) &&
      year !== undefined
    ) {
      const range = transactionYearDateRange(year);
      startDate = range.start;
      endDate = range.endExclusive;
      year = undefined;
    } else if (startDate && endDate) {
      // Custom range: treat UI end as inclusive day → send next calendar day as exclusive upper bound.
      const endInclusive = endDate.slice(0, 10);
      const endParts = endInclusive.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (endParts) {
        const endDay = new Date(
          Number(endParts[1]),
          Number(endParts[2]) - 1,
          Number(endParts[3])
        );
        endDay.setDate(endDay.getDate() + 1);
        const y = endDay.getFullYear();
        const m = String(endDay.getMonth() + 1).padStart(2, '0');
        const d = String(endDay.getDate()).padStart(2, '0');
        boundStart = startDate.slice(0, 10);
        boundEndExclusive = `${y}-${m}-${d}`;
        endDate = boundEndExclusive;
      }
      // Never send month/year alongside an explicit range (backend 400s).
      month = undefined;
      year = undefined;
    }

    const restFilters: TransactionFilters = { ...filters };
    delete restFilters.month;
    delete restFilters.year;
    delete restFilters.start_date;
    delete restFilters.end_date;

    const effectiveFilters: TransactionFilters = {
      ...restFilters,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      ...(month !== undefined ? { month } : {}),
      ...(year !== undefined ? { year } : {}),
    };

    // Create cache key (include organizationId so cache is per-org, no stale data on org switch)
    const { organizationId: _orgId, skipCache, ...cacheFilters } = effectiveFilters;
    const cacheKey = createApiCacheKey('/transactions', {
      ...cacheFilters,
      organizationId: _orgId ?? 'none'
    });
    
    const generationAtStart = transactionsListCacheGeneration;

    // Check cache first (ignored after mutations bump the generation)
    if (!skipCache) {
      const cached = getCachedApiResponse<TransactionsResponse>(cacheKey);
      if (cached) {
        logger.debug('Transactions served from cache', 'TransactionsService.getTransactions', { 
          cacheKey: cacheKey.substring(0, 50) + '...'
        });
        return cached;
      }
    }

    // Backend: type=all & category=all mean “no filter” (always send explicit values)
    const params = new URLSearchParams();
    if (effectiveFilters.page) params.append('page', String(effectiveFilters.page));
    if (effectiveFilters.limit) params.append('limit', String(effectiveFilters.limit));

    const typeParam = !effectiveFilters.type || effectiveFilters.type === 'all' ? 'all' : effectiveFilters.type;
    const categoryParam = !effectiveFilters.category || effectiveFilters.category === 'all' ? 'all' : effectiveFilters.category;
    const paymentParam =
      !effectiveFilters.payment_method || effectiveFilters.payment_method === 'all'
        ? 'all'
        : effectiveFilters.payment_method;
    params.append('type', typeParam);
    params.append('category', categoryParam);
    params.append('payment_method', paymentParam);

    // Period filters must use transaction_date (booking date), never payable/receivable due dates.
    // Unpaid expense with July transaction_date + August payable_due_date belongs in July filters/reports.
  const hasDateRange = Boolean(effectiveFilters.start_date && effectiveFilters.end_date);
  if (effectiveFilters.start_date) params.append('start_date', effectiveFilters.start_date);
  if (effectiveFilters.end_date) params.append('end_date', effectiveFilters.end_date);
  if (hasDateRange) {
    params.append('date_field', 'transaction_date');
  }
    // Backend uses 1-based month (1=Jan). Do not combine month/year with start/end — returns 400.
    if (!hasDateRange) {
      if (effectiveFilters.month !== undefined && effectiveFilters.month >= 0) {
        params.append('month', String(effectiveFilters.month + 1));
      }
      if (effectiveFilters.year !== undefined) params.append('year', String(effectiveFilters.year));
    }
    if (effectiveFilters.search) params.append('search', effectiveFilters.search);

      try {
        const response = await api.get(`/transactions?${params}`);

        const data = response.data;
        const payload = data.data as TransactionsListData | undefined;
      
      // Backend returns: { success: true, data: { transactions: [...], pagination: {...}, totals: {...} } }
      const transactionsArray = payload?.transactions || [];

      // Transform backend data to frontend format (list may be minimal: id, type, amount, status, created_at)
      let transactions: TransactionItem[] = (transactionsArray as any[]).map((t: any) =>
        transactionItemFromApiRecord(t)
      );

      if (boundStart && boundEndExclusive) {
        transactions = filterTransactionsByHalfOpenDateRange(
          transactions,
          boundStart,
          boundEndExclusive
        );
      }

      const backendTotals = payload?.totals;
      const backendPagination = payload?.pagination;
      const backendCategories = payload?.categories;

      const zeroTotals = { income: 0, expense: 0, net: 0 };
      if (!backendTotals) {
        logger.warn('Transactions API missing totals — KPIs may be wrong until backend sends data.data.totals', 'TransactionsService.getTransactions');
      }

      const result = {
        transactions,
        pagination: backendPagination ?? {
          page: effectiveFilters.page || 1,
          limit: effectiveFilters.limit || 20,
          total: 0,
        },
        totals: backendTotals ?? zeroTotals,
        categories: backendCategories?.length
          ? backendCategories
          : Array.from(new Set(transactions.map(t => t.category))),
      };

      // Never re-seed apiCache with a response that started before a mutation invalidation.
      if (!skipCache && generationAtStart === transactionsListCacheGeneration) {
        cacheApiResponse(cacheKey, result, 2 * 60 * 1000);
      }
      
      logger.debug('Transactions fetched and cached successfully', 'TransactionsService.getTransactions', { 
        count: transactions.length,
        filters: effectiveFilters,
        cacheWritten: !skipCache && generationAtStart === transactionsListCacheGeneration,
      });
      
      return result;
      } catch (error) {
        logger.warn('API call failed', 'TransactionsService.getTransactions', error);
        // Rethrow so frontend shows real error state (e.g. "Failed to load transactions")
        // instead of misleading mock data
        throw error;
      }
  },

  /**
   * Aggregate totals for KPI cards without transferring hundreds of rows.
   * Relies on backend `totals` for the filtered date range (limit=1 minimizes payload).
   */
  async getTransactionTotals(
    filters: Pick<
      TransactionFilters,
      | 'start_date'
      | 'end_date'
      | 'month'
      | 'year'
      | 'type'
      | 'category'
      | 'payment_method'
      | 'search'
      | 'organizationId'
    >
  ): Promise<TransactionsResponse['totals']> {
    const listFilters: TransactionFilters = {
      page: 1,
      limit: 1,
      type: filters.type ?? 'all',
      category: filters.category ?? 'all',
      payment_method: filters.payment_method ?? 'all',
      ...(filters.year !== undefined ? { year: filters.year } : {}),
      ...(filters.start_date ? { start_date: filters.start_date } : {}),
      ...(filters.end_date ? { end_date: filters.end_date } : {}),
      ...(filters.month !== undefined && filters.month >= 0 ? { month: filters.month } : {}),
      ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
    };
    if (filters.organizationId != null) listFilters.organizationId = filters.organizationId;
    const res = await this.getTransactions(listFilters);
    return res.totals;
  },

  /**
   * @method addTransaction
   * @description Creates a new transaction
   * @param data - Transaction data to create
   * @returns Promise<TransactionItem> - Created transaction
   * @throws Error if creation fails
   * @example
   * ```typescript
   * const transaction = await transactionsService.addTransaction({
   *   description: 'Office Supplies',
   *   amount: -150.00,
   *   type: 'expense',
   *   category: 'Supplies'
   * });
   * ```
   */
  async addTransaction(data: Record<string, unknown>): Promise<TransactionItem> {
    const response = await api.post('/transactions', data);
    const body = response.data as Record<string, unknown>;
    const payload = (body.data ?? body) as
      | Record<string, unknown>
      | CreateExpenseOrchestrationResponse
      | CreateIncomeOrchestrationResponse;

    if (payload && typeof payload === 'object' && 'transaction' in payload && payload.transaction) {
      return transactionItemFromApiRecord(payload.transaction as Record<string, unknown>);
    }

    return transactionItemFromApiRecord(payload as Record<string, unknown>);
  },

  async patchTransaction(
    id: string,
    data: Record<string, unknown>
  ): Promise<TransactionItem> {
    const response = await api.patch(`/transactions/${id}`, data);
    const body = response.data as Record<string, unknown>;
    const payload = (body.data ?? body) as Record<string, unknown> | CreateIncomeOrchestrationResponse;

    if (payload && typeof payload === 'object' && 'transaction' in payload && payload.transaction) {
      return transactionItemFromApiRecord(payload.transaction as Record<string, unknown>);
    }

    return transactionItemFromApiRecord(payload as Record<string, unknown>);
  },

  /**
   * @method updateTransaction
   * @description Updates an existing transaction
   * @param id - ID of transaction to update
   * @param data - Updated transaction data
   * @returns Promise<TransactionItem> - Updated transaction
   * @throws Error if update fails
   */
  async updateTransaction(id: string, data: Record<string, unknown>): Promise<TransactionItem> {
    const response = await api.put(`/transactions/${id}`, data);
    const body = response.data as Record<string, unknown>;
    const payload = (body.data ?? body) as Record<string, unknown>;
    const t = (
      payload &&
      typeof payload === 'object' &&
      payload.transaction &&
      typeof payload.transaction === 'object'
        ? payload.transaction
        : payload
    ) as Record<string, unknown>;
    return transactionItemFromApiRecord(t);
  },

  /**
   * @method deleteTransaction
   * @description Deletes a transaction
   * @param id - ID of transaction to delete
   * @returns Promise<void>
   * @throws Error if deletion fails
   */
  async deleteTransaction(id: string): Promise<void> {
    await api.delete(`/transactions/${id}`);
  },

  /**
   * @method addComment
   * @description Adds a comment to a transaction
   * @param id - ID of transaction to comment on
   * @param comment - Comment text
   * @returns Promise<any> - Comment data
   * @throws Error if comment creation fails
   */
  async addComment(id: string, comment: string): Promise<any> {
    // Extract mentions from comment content
    const mentionPattern = /@(\w+)/g;
    const matches = comment.match(mentionPattern);
    const mentions = matches ? matches.map(match => match.slice(1)) : [];

    // Backend implementation
    const response = await api.post('/comments', { 
        entity_type: 'transaction',
        entity_id: id,
        content: comment,
        mentions: mentions
      });
      return response.data.data;
  },

  /**
   * @method flagTransaction
   * @description Flags a transaction for director/investor review
   * @param transactionId - ID of transaction to flag
   * @param data - Flag data (reason, priority, flagged_for_user_id)
   * @returns Promise<any> - Flag data
   * @throws Error if flagging fails
   */
  async flagTransaction(transactionId: string, data: { reason: string; priority?: string; flagged_for_user_id?: string }): Promise<any> {
    const response = await api.post(`/transactions/${transactionId}/flag`, data);
    return response.data.data;
  },

  /**
   * @method getTransactionFlags
   * @description Gets all flags for a specific transaction
   * @param transactionId - ID of transaction
   * @returns Promise<any[]> - Array of flags
   * @throws Error if fetch fails
   */
  async getTransactionFlags(transactionId: string): Promise<any[]> {
    const response = await api.get(`/transactions/${transactionId}/flags`);
    return response.data.data;
  },

  /**
   * @method getFlaggedTransactions
   * @description Gets all flagged transactions with optional filters
   * @param filters - Optional filters (status, priority)
   * @returns Promise<any[]> - Array of flagged transactions
   * @throws Error if fetch fails
   */
  async getFlaggedTransactions(filters?: { status?: string; priority?: string }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    
    const response = await api.get(`/transactions/flagged/all?${params}`);
    return response.data.data;
  },

  /**
   * @method updateTransactionFlag
   * @description Updates a transaction flag (status, additional details)
   * @param transactionId - ID of transaction
   * @param flagId - ID of flag
   * @param data - Update data (status, additional_details)
   * @returns Promise<any> - Updated flag data
   * @throws Error if update fails
   */
  async updateTransactionFlag(transactionId: string, flagId: string, data: { status?: string; additional_details?: string }): Promise<any> {
    const response = await api.put(`/transactions/${transactionId}/flags/${flagId}`, data);
    return response.data.data;
  },

  /**
   * @method resolveTransactionFlag
   * @description Resolves a transaction flag
   * @param transactionId - ID of transaction
   * @param flagId - ID of flag
   * @param additionalDetails - Optional additional details
   * @returns Promise<any> - Resolved flag data
   * @throws Error if resolution fails
   */
  async resolveTransactionFlag(transactionId: string, flagId: string, additionalDetails?: string): Promise<any> {
    const response = await api.post(`/transactions/${transactionId}/flags/${flagId}/resolve`, {
      additional_details: additionalDetails
    });
    return response.data.data;
  },
};
